import React,{ 
  useCallback,
  useRef,
  useMemo
} from "react";
import { unstable_batchedUpdates } from "react-dom";

import { Text, Button } from "@aidron/aidron-ds";

import { i18n } from "../../../../../translate/i18n";

import { CustomCard } from "../../../../../components/StyledComponents/styleComponents.index";

import { DoubleArrow } from "../../../../../components/SharedComponents/sharedComponents.index";

import PropTypes from 'prop-types';

import { useHierarchyFilterViewModel } from "../../../../../viewModel/useHierarchyFilterViewModel";
import { COLUMNS_LABELS, DEFAULT_VALUES, TENANT_CATEGORY } from "../../../../../components/components.constants";
import { textFormatter } from "../../../../../utils/formatter/formatter";

const { t } = i18n;

const FILTER_BOX_UI_STATE = { allowOneOptionForSelection: false };

const FilterBoxMemo = React.memo(function FilterBox({ actions, ctx, uiState }) {

  const { setSelectedValues } = actions || {};

  const {
    name, 
    options, 
    selectedValues,
    possibleValues
  } = ctx || {};

  const { allowOneOptionForSelection } = uiState || {}

  const scrollableDivRef = useRef(null);

  const scrollToTop = () => scrollableDivRef.current.scrollTo({ top: 0 });

  const onClick = (option) => {
    let newSelectedValues = selectedValues ? [...selectedValues] : [];

    if (newSelectedValues.includes(option.name)) {
      const index = newSelectedValues.indexOf(option.name);
      newSelectedValues.splice(index, 1);
    } else {
      if (allowOneOptionForSelection) {
        newSelectedValues = [option.name];
      } else {
        newSelectedValues.push(option.name);
      }
    }

    setSelectedValues(newSelectedValues);
    scrollToTop();
  };

  const sortedOptions = useMemo(() => {
    const sorted = [...options];

    sorted.sort((a, b) => {
      const nameA = a?.name ?? '';
      const nameB = b?.name ?? '';
      return nameA.localeCompare(nameB);
    });

    if (selectedValues && selectedValues.length > 0) {
      const selected = [];
      const unselected = [];
      
      sorted.forEach(option => {
        if (selectedValues.includes(option.name)) {
          selected.push(option);
        } else {
          unselected.push(option);
        }
      });

      return [...selected, ...unselected];
    }
    
    return sorted;
  }, [options, selectedValues]);

  return (
    <div style={{ backgroundColor: "#f5f5f7" }} className="d-flex flex-column justify-content-between width-fit-content border-radius-8px box-shadow-black-2-0-11-0 mb-3 me-2 px-3 pb-2">
      <Text weight="bold">{name}</Text>
      
      <div className="d-flex justify-content-center pb-1">
        <div className="width-180px border-top-light-grey-solid-1px me-2"></div>
      </div>

      <div className="height-175px width-200px overflow-scroll pe-2" ref={scrollableDivRef}>
        {sortedOptions.map((option) => {
          const isSelected = selectedValues?.includes(option.name);
          const formattedOption = option.name === DEFAULT_VALUES.NOT_APPLY 
            ? textFormatter(option.name, { textTransform: 'uppercase'}) 
            : textFormatter(option.name)

          return (
            <Button
              label={formattedOption}
              onClick={() => onClick(option)}
              type={isSelected ? "outlined" : "ghost"}
              className="box-shadow-unset box-shadow-unset-on-button text-align-start-on-button text-align-start"
              disabled={allowOneOptionForSelection ? !possibleValues.includes(option.name) : false}
              key={option.name}
            />
          );
        })}
      </div>
    </div>
  );
});

const FilterBoxItem = React.memo(function FilterBoxItem({ level, label, vm, handleSetSelectedValues }) {
  const actions = useMemo(() => ({
    setSelectedValues: (values) => handleSetSelectedValues(level, values)
  }), [level, handleSetSelectedValues]);

  const ctx = useMemo(() => ({
    name: label,
    options: vm.ctx.generateOptions(level),
    selectedValues: vm.uiState.selectedLevels[level] || [],
    possibleValues: vm.ctx.getPossibleValues(level)
  }), [level, label, vm.ctx.generateOptions, vm.uiState.selectedLevels, vm.ctx.getPossibleValues]);

  return <FilterBoxMemo actions={actions} ctx={ctx} uiState={FILTER_BOX_UI_STATE} />;
});

export function HierarchyFiltersSalesRecommendation({ actions, ctx, uiState }) {

  const { 
    setSelectedProductIds  = () => {},
    setAppliedFiltersCount = () => {},
    setShowFilters = () => {},
    setSelectedHierarchyFilters = () => {},
    setSelectedSupplierFromFilter = () => {},
    setAppliedLevels: setExternalAppliedLevels,
    setSelectedLevels: setExternalSelectedLevels, 
    setTypedExpression: setExternalTypedExpression,
    setAppliedExpression: setExternalAppliedExpression
  } = actions || {};  
  
  const {
    productIds,
    suppliersAndProductRelations = [],
    hierarchyNames = { levels: [], labels: {} },
    products
  } = ctx || {};

  const { 
    showFilters, 
    clientType = TENANT_CATEGORY.GAS_STATION,
    shouldDisplayDoubleArrowButton = true,
    hasExternalFilters,
    shoulApplyMargin = true,
    shoulApplyDefaultInlineSize = true
  } = uiState || {};

  const vm = useHierarchyFilterViewModel({
    actions: {
      setSelectedProductIds,
      setAppliedFiltersCount,
      setSelectedHierarchyFilters,
      setSelectedSupplierFromFilter,
      setAppliedLevels: setExternalAppliedLevels, 
      setSelectedLevels: setExternalSelectedLevels,
      setTypedExpression: setExternalTypedExpression,
      setAppliedExpression: setExternalAppliedExpression
    },
    ctx: { 
      suppliersAndProductRelations, 
      hierarchyNames, 
      productIds,
      allProducts: products
    },
    uiState: {
      allowOneOptionForSelection: false,
      appliedLevels: uiState?.appliedLevels,
      selectedLevels: uiState?.selectedLevels,
      typedExpression: uiState?.typedExpression,
      appliedExpression: uiState?.appliedExpression
    }
  }) 

  const handleSetSelectedValues = useCallback(
    (level, values) => {
      unstable_batchedUpdates(() => {
        if (uiState.clientType === TENANT_CATEGORY.RETAIL) {
          vm.actions.setSelectedLevels(prev => ({ ...prev, [level]: values }));
          vm.actions.setAppliedLevels(prev => ({ ...prev, [level]: values }));
        } else {
          vm.actions.setSelectedLevels({ 
            ...vm.uiState.selectedLevels, 
            [level]: values 
          });
          vm.actions.setAppliedLevels({ 
            ...vm.uiState.selectedLevels, 
            [level]: values 
          });
        }
      });
    },
    [
      vm.actions.setSelectedLevels,
      vm.actions.setAppliedLevels,
      uiState.clientType,
      vm.uiState.selectedLevels
    ]
  );

  return (
    <CustomCard
      margin={shoulApplyMargin ? "1rem auto" : ""}
      padding="10px 30px"
      backgroundColor="blueSoft"
      display={showFilters ? "" : "none"}
      inlineSize={shoulApplyDefaultInlineSize ? "70%" : ""}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', inlineSize: "100%" }}>

        <div style={{ display: 'flex', flexDirection: 'column', marginBlockEnd: '0.5rem' }}>
          <Text size="sm" weight="bold">
            {t("searchByHierarchy")}
          </Text>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              style={{
                borderColor:' rgba(17, 24, 39, 0.125)',
                borderRadius: '0.6rem',
                blockSize: 'auto',
                borderWidth: '0.1rem',
                boxSizing: 'border-box',
                color: 'rgb(153, 153, 153)',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '16.1px',
                padding: '0.8em',
                inlineSize: 'fit-content'
              }}
              onChange={(e) => vm.actions.setTypedExpression(e.target.value)}
              onBlur={() => vm.actions.setTypedExpression(vm.uiState.typedExpression || "")}
              placeholder={t("enterSearchTerm")}
              type='text'
              value={vm.uiState.typedExpression}
            />
          </div>
        </div>        

        <Button
          label={t("clearFilters")}
          type="filled"
          icon="x"
          position="left"
          onClick={() => {
            vm.actions.resetFilters();
            if (hasExternalFilters) {
              setSelectedProductIds([]);
              setAppliedFiltersCount(0);
            }
          }}
          disabled={!vm.actions.isAnyFilterApplied() && !hasExternalFilters}
        />
      </div>

      <Text size="sm" weight="bold" className="mb-2">
        {t("filterByHierarchy")}
      </Text>

      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflowX: 'scroll',
        inlineSize: "96%"
      }}>

        <section className="d-flex" style={{ overflowX: 'scroll' }}>

          {clientType === TENANT_CATEGORY.GAS_STATION && (
            <>
              <FilterBoxItem
                key={COLUMNS_LABELS.SUPPLIER}
                level={COLUMNS_LABELS.SUPPLIER}
                label={COLUMNS_LABELS.SUPPLIER}
                vm={vm}
                handleSetSelectedValues={handleSetSelectedValues}
              />

              {hierarchyNames.levels
                .filter(level => hierarchyNames.labels[level] !== "Grupo")
                .map((level) => (
                  <FilterBoxItem
                    key={level}
                    level={level}
                    label={hierarchyNames.labels[level]}
                    vm={vm}
                    handleSetSelectedValues={handleSetSelectedValues}
                  />
                ))}
            </>
          )}

          {clientType === TENANT_CATEGORY.RETAIL &&
            hierarchyNames.levels.map((level) => (
              <FilterBoxItem
                key={level}
                level={level}
                label={hierarchyNames.labels[level]}
                vm={vm}
                handleSetSelectedValues={handleSetSelectedValues}
              />
            ))
          }

        </section>        

        {shouldDisplayDoubleArrowButton && (
          <DoubleArrow 
            width={40}
            height={40}
            onClick={() => setShowFilters(prev => !prev)}
          />
        )}

      </div>
    </CustomCard>
  );
};

FilterBoxMemo.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
};

FilterBoxItem.propTypes = {
  level: PropTypes.string,
  label: PropTypes.string,
  vm: PropTypes.shape(),
  handleSetSelectedValues: PropTypes.func,
};

HierarchyFiltersSalesRecommendation.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};